/**
 * QUIPWA — TasksService
 *
 * Gestiona tareas de estudio en Google Tasks en lugar de eventos de Calendar.
 * Las Tasks tienen estado "completado", lo que elimina el bucle de eventos
 * que nunca desaparecían del calendario.
 *
 * Flujo:
 *   - Al TERMINAR sesión: crear tarea pendiente para la próxima fecha de revisión
 *   - Al INICIAR sesión: marcar la tarea previa como completada (si existe)
 *
 * La tarea guarda el deckInstanceId en las notas para poder linkear de vuelta.
 *
 * Endpoint base: https://tasks.googleapis.com/tasks/v1
 * Scope requerido: https://www.googleapis.com/auth/tasks
 *
 * IMPORTANTE: Si el token no tiene el scope 'tasks', todos los métodos fallan
 * silenciosamente (return null / []). El usuario debe re-autenticar una vez
 * después de que se añadió el scope.
 */

import { authSession, SCOPES } from '../auth/index.ts'

const TASKS_API = 'https://tasks.googleapis.com/tasks/v1'
const TASKLIST_TITLE = 'QUIPWA — Repasos'

// ─── TYPES ───────────────────────────────────────────────────────────────────

export interface GTask {
  id: string
  title: string
  notes?: string
  due?: string        // RFC 3339 — Google Tasks solo usa la parte de fecha (00:00:00.000Z)
  status: 'needsAction' | 'completed'
  selfLink?: string
}

export interface GTaskList {
  id: string
  title: string
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────

/**
 * Converts a Unix timestamp (seconds) to an RFC 3339 date string in UTC
 * for use as a Google Tasks "due" field.
 *
 * CRITICAL: Use local date components instead of UTC to avoid timezone shifts
 * that can push the task to the next day if created late at night.
 */
function toTasksDueISO(unixSec: number): string {
  const d = new Date(unixSec * 1000)
  return new Date(
    Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())
  ).toISOString()
}

// ─── SERVICE ─────────────────────────────────────────────────────────────────

export class TasksService {
  /** In-memory cache of the QUIPWA task list ID. Reset on 404 to force re-discovery. */
  private taskListId: string | null = null

  // ── Auth ─────────────────────────────────────────────────────────────────

  private async authHeaders(): Promise<{ Authorization: string; 'Content-Type': string }> {
    const token = await authSession.getAccessToken()
    if (!token) throw new Error('TasksService: no active Google session')
    // Reminders are opt-in: without the Tasks permission every call is a no-op failure.
    if (!authSession.hasScope(SCOPES.tasks)) throw new Error('TasksService: Tasks permission not granted')
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
  }
  // ── TaskList management ──────────────────────────────────────────────────

  /**
   * Finds or creates the QUIPWA task list.
   * Caches the list ID in memory; clears the cache on 404 (stale ID).
   *
   * Common failure reasons:
   *   403 → token doesn't have 'tasks' scope (user needs to re-auth)
   *   404 → list was deleted; this call will create a new one
   */
  async ensureTaskList(): Promise<string> {
    if (this.taskListId) return this.taskListId

    const headers = await this.authHeaders()
    const res = await fetch(`${TASKS_API}/users/@me/lists`, { headers })

    if (!res.ok) {
      const errBody = await res.text().catch(() => '')
      if (res.status === 401 || res.status === 403) {
        console.warn(
          `[TasksService] ensureTaskList: ${res.status} — token likely missing 'tasks' scope.\n` +
          'User must sign out and sign in again to grant Tasks permissions.\n' +
          errBody.slice(0, 300)
        )
      }
      throw new Error(`TasksService.ensureTaskList: HTTP ${res.status}`)
    }

    const data = await res.json() as { items?: GTaskList[] }
    const existing = (data.items ?? []).find(l => l.title === TASKLIST_TITLE)
    if (existing) {
      this.taskListId = existing.id
      return existing.id
    }

    // No list yet — create one
    const createRes = await fetch(`${TASKS_API}/users/@me/lists`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ title: TASKLIST_TITLE })
    })
    if (!createRes.ok) {
      const errBody = await createRes.text().catch(() => '')
      throw new Error(`TasksService.createList: HTTP ${createRes.status}\n${errBody.slice(0, 200)}`)
    }
    const created = await createRes.json() as GTaskList
    this.taskListId = created.id
    console.info('[TasksService] Created task list:', created.id, created.title)
    return created.id
  }

  // ── Fetching with stale-list-id retry ────────────────────────────────────

  /**
   * Perform a fetch against /lists/{listId}/... with automatic retry on 404.
   * A 404 means the cached listId is stale — clear it and re-discover.
   */
  private async fetchWithListRetry(
    buildUrl: (listId: string) => string,
    options: RequestInit
  ): Promise<Response> {
    const listId = await this.ensureTaskList()
    const res = await fetch(buildUrl(listId), options)

    if (res.status === 404) {
      // Stale list ID — reset cache and retry once
      console.warn('[TasksService] Got 404 for list', listId, '— resetting cache and retrying.')
      this.taskListId = null
      const newListId = await this.ensureTaskList()
      return fetch(buildUrl(newListId), options)
    }

    return res
  }

  // ── Task CRUD ────────────────────────────────────────────────────────────

  /**
   * Create a study reminder task for a deck.
   *
   * @param deckName       Display name of the deck.
   * @param deckInstanceId Internal ID — stored in notes for deep-link and matching.
   * @param dueDateTs      Unix timestamp (seconds) for the next review date.
   * @param estimatedCards Cards estimated for next session.
   * @returns The created GTask, or null if the operation failed (non-fatal).
   */
  async createStudyTask(
    deckName: string,
    deckInstanceId: string,
    dueDateTs: number,
    estimatedCards: number
  ): Promise<GTask | null> {
    try {
      const headers  = await this.authHeaders()
      // Default to 09:00 as suggested time since calendar_event_time was removed
      const [h, m]   = [9, 0]

      // Use local date components — ensures "May 4th 10pm local" becomes "May 4th UTC 00:00"
      const dueDateISO = toTasksDueISO(dueDateTs)
      const timeLabel  = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
      const deepLink   = `${window.location.origin}/#/deck/${deckInstanceId}/session`

      const body = {
        title: `📚 QUIPWA: ${deckName}`,
        notes: [
          `${estimatedCards} tarjeta${estimatedCards !== 1 ? 's' : ''} programada${estimatedCards !== 1 ? 's' : ''}`,
          `Hora sugerida: ${timeLabel}`,
          `Iniciar sesión: ${deepLink}`,
          `deck_instance_id:${deckInstanceId}`,
          'Creado automáticamente por QUIPWA'
        ].join('\n'),
        due:    dueDateISO,
        status: 'needsAction'
      }

      const res = await this.fetchWithListRetry(
        (listId) => `${TASKS_API}/lists/${listId}/tasks`,
        { method: 'POST', headers, body: JSON.stringify(body) }
      )

      if (!res.ok) {
        const errText = await res.text().catch(() => '')
        console.warn('[TasksService] createStudyTask failed:', res.status, errText.slice(0, 300))
        if (res.status === 401 || res.status === 403) {
          console.warn(
            '[TasksService] ⚠ Missing "tasks" scope. ' +
            'User must sign out and sign in again to grant Tasks permissions.'
          )
        }
        return null
      }

      return res.json() as Promise<GTask>
    } catch (err) {
      console.warn('[TasksService] createStudyTask error:', err)
      return null
    }
  }

  /**
   * Mark a task as completed (called when a session starts).
   */
  async completeTask(taskId: string): Promise<boolean> {
    try {
      const headers = await this.authHeaders()

      const res = await this.fetchWithListRetry(
        (listId) => `${TASKS_API}/lists/${listId}/tasks/${encodeURIComponent(taskId)}`,
        { method: 'PATCH', headers, body: JSON.stringify({ status: 'completed' }) }
      )
      return res.ok
    } catch {
      return false
    }
  }

  /**
   * Get a specific task by its ID (for the NextEventBanner after session).
   */
  async getTask(taskId: string): Promise<GTask | null> {
    try {
      const headers = await this.authHeaders()

      const res = await this.fetchWithListRetry(
        (listId) => `${TASKS_API}/lists/${listId}/tasks/${encodeURIComponent(taskId)}`,
        { headers }
      )
      if (!res.ok) return null
      return res.json() as Promise<GTask>
    } catch {
      return null
    }
  }

  /**
   * List pending (needsAction) study tasks.
   */
  async listPendingTasks(maxResults = 20): Promise<GTask[]> {
    try {
      const headers = await this.authHeaders()
      const params  = new URLSearchParams({
        showCompleted: 'false',
        showHidden:    'false',
        maxResults:    String(maxResults),
        orderBy:       'due'
      })

      const res = await this.fetchWithListRetry(
        (listId) => `${TASKS_API}/lists/${listId}/tasks?${params}`,
        { headers }
      )
      if (!res.ok) return []
      const data = await res.json() as { items?: GTask[] }
      return data.items ?? []
    } catch {
      return []
    }
  }

  /**
   * Find the pending task for a specific deck by matching deckInstanceId in notes.
   */
  async findPendingTaskForDeck(deckInstanceId: string): Promise<GTask | null> {
    try {
      const tasks = await this.listPendingTasks(50)
      return tasks.find(t => t.notes?.includes(`deck_instance_id:${deckInstanceId}`)) ?? null
    } catch {
      return null
    }
  }

  /**
   * List recently completed QUIPWA tasks.
   */
  async listCompletedTasks(days = 7, maxResults = 20): Promise<GTask[]> {
    try {
      const headers = await this.authHeaders()
      const params  = new URLSearchParams({
        showCompleted: 'true',
        showHidden:    'false',
        maxResults:    String(maxResults)
      })

      const res = await this.fetchWithListRetry(
        (listId) => `${TASKS_API}/lists/${listId}/tasks?${params}`,
        { headers }
      )
      if (!res.ok) return []
      const data = await res.json() as { items?: GTask[] }
      const cutoff = Date.now() - days * 86_400_000
      return (data.items ?? []).filter(t => {
        if (t.status !== 'completed') return false
        if (!t.due) return true
        return new Date(t.due).getTime() > cutoff
      })
    } catch {
      return []
    }
  }
}

export const tasksService = new TasksService()
